import { HttpResponseError } from 'lib/error/http-response-error';
import { createHttpResponseError } from './utils';
import { AbortError } from 'p-retry';
import * as Sentry from '@sentry/react';
import { getTokenPrefix } from 'lib/utils/embedded-app.utils';

const TOKEN_ACCESSOR_ERROR_MESSAGE = '_tokenAccessor called before initializiation';
const TOKEN_ACCESSOR_ERROR_NAME = 'PrivateFetchTokenAccessorError';

class PrivateFetch {
    private _tokenAccessor: (() => Promise<string>) | null = null;

    set tokenAccessor(getAccessTokenSilently: PrivateFetch['_tokenAccessor']) {
        this._tokenAccessor = getAccessTokenSilently;
    }

    public async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response | undefined> {
        try {
            if (!this._tokenAccessor) {
                const tokenAccessorError = new Error(TOKEN_ACCESSOR_ERROR_NAME);
                tokenAccessorError.message = TOKEN_ACCESSOR_ERROR_MESSAGE;
                tokenAccessorError.name = TOKEN_ACCESSOR_ERROR_NAME;
                const stackTail = init ? JSON.stringify(init) : '';
                tokenAccessorError.stack =
                    'input: ' + (input?.toString ? input.toString() : `${input}`) + ', init: ' + (stackTail || '-');
                throw tokenAccessorError;
            }

            const accessToken = await this._tokenAccessor()
                .then(token => token)
                .catch(e => {
                    if (e?.error === 'login_required' || e.error === 'consent_required') {
                        return Promise.reject(
                            createHttpResponseError('Login required', { status: 944 } as any, {
                                message: 'Login required',
                            }),
                        );
                    }
                    throw e;
                });

            const initSettings = init ? init : {};
            const initHeaders = initSettings.headers ? initSettings.headers : {};

            const resolvedInit = {
                ...initSettings,
                headers: {
                    ...initHeaders,
                    Authorization: `${getTokenPrefix()} ${accessToken}`,
                },
            };

            return await window
                .fetch(input, resolvedInit)
                .then(async response => {
                    if (response.status === 200 || response.status === 204 || response.status === 201) {
                        return response;
                    }

                    if (response.status === 401) {
                        return Promise.reject(
                            createHttpResponseError(
                                `${response.status} ${response.statusText}`,
                                response,
                                await response.json(),
                            ),
                        );
                    } else if (response.status === 400 || response.status === 422) {
                        return Promise.reject(
                            createHttpResponseError(
                                `${response.status} ${response.statusText}`,
                                response,
                                await response.json(),
                            ),
                        );
                    }

                    const resolvedResponseBody =
                        Object.entries((await response.json()) || {}).length && response.status === STATUS_418_CODE
                            ? await response.json()
                            : {};

                    return Promise.reject(
                        createHttpResponseError(
                            `${response.status} ${response.statusText}`,
                            response,
                            resolvedResponseBody,
                        ),
                    );
                })
                .catch(err => {
                    if (err instanceof HttpResponseError || err instanceof AbortError) {
                        return Promise.reject(err);
                    }
                    return Promise.reject(
                        createHttpResponseError('Unknown error occurred', { status: null } as any, { message: err }),
                    );
                });
        } catch (error) {
            Sentry.captureException(error);
        }
    }
}

const privateFetch = new PrivateFetch();

const STATUS_418_CODE = 418;

export default privateFetch;
